import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX,
  GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX,
} from '~/components/GHADiscountConfig/constants';

import type {
  GHADiscountRuleAccountType,
  GHADiscountScopeRule,
  GHADiscountScopeRuleBody,
  GHAScopeRuleCugTier,
  GHAScopeRuleDiscountType,
} from '~/services/ConnectionGoogleService';

import ScopeDimensionFields from './ScopeDimensionFields';
import { GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS, GHA_SCOPE_DISCOUNT_TYPE_OPTIONS } from './constants';
import { normaliseScopeDimensions } from './utils';

interface Props {
  open: boolean;
  /** Present = edit, which the backend executes as replace (archive + create). */
  initialRule?: GHADiscountScopeRule | null;
  /** Where a brand-new rule lands by default: after the current last priority. */
  defaultPriority: number;
  isSaving: boolean;
  /** Registration-validation 400s render here, next to the fields they complain about. */
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (input: GHADiscountScopeRuleBody) => void;
}

// Every dimension is a list: values within one dimension are OR, dimensions AND together;
// an empty list means "match everything on this dimension" (sent as an omitted key).
interface FormState {
  accountType: GHADiscountRuleAccountType;
  suppliers: Array<string>;
  chainNames: Array<string>;
  cugTiers: Array<GHAScopeRuleCugTier>;
  userCountries: Array<string>;
  discountType: GHAScopeRuleDiscountType;
  discountValue: string;
  priority: string;
  minAdvanceBookingWindow: string;
  minLengthOfStay: string;
}

const toFormState = (rule: GHADiscountScopeRule | null | undefined, defaultPriority: number): FormState => ({
  accountType: rule?.accountType ?? 'lpp',
  suppliers: rule?.suppliers ?? [],
  chainNames: rule?.chainNames ?? [],
  cugTiers: rule?.cugTiers ?? [],
  userCountries: rule?.userCountries ?? [],
  discountType: rule?.discountType ?? 'percentage',
  discountValue: rule == null ? '' : String(rule.discountValue),
  priority: String(rule?.priority ?? defaultPriority),
  minAdvanceBookingWindow: String(rule?.minAdvanceBookingWindow ?? 0),
  minLengthOfStay: String(rule?.minLengthOfStay ?? 1),
});

export default function ScopeRuleDialog({
  open,
  initialRule,
  defaultPriority,
  isSaving,
  submitError,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(initialRule, defaultPriority));
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset only when the dialog (re)opens — defaultPriority derives from a query that refetches on
  // window focus, and resetting on its change would wipe a form someone tabbed away from.
  useEffect(() => {
    if (open) {
      setForm(toFormState(initialRule, defaultPriority));
      setValidationError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialRule]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = () => {
    const discountValue = Number(form.discountValue);
    const priority = Number(form.priority);
    const minAdvanceBookingWindow = Number(form.minAdvanceBookingWindow);
    const minLengthOfStay = Number(form.minLengthOfStay);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      setValidationError('Discount value must be a positive number');
      return;
    }
    if (!Number.isInteger(priority) || priority < 1) {
      setValidationError('Priority must be a positive integer');
      return;
    }
    if (
      !Number.isInteger(minAdvanceBookingWindow) ||
      minAdvanceBookingWindow < 0 ||
      minAdvanceBookingWindow > GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX
    ) {
      setValidationError(`Min ABW must be a whole number 0-${GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX}`);
      return;
    }
    if (
      !Number.isInteger(minLengthOfStay) ||
      minLengthOfStay < 1 ||
      minLengthOfStay > GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX
    ) {
      setValidationError(`Min LOS must be a whole number 1-${GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX}`);
      return;
    }
    const dims = normaliseScopeDimensions(form);
    if (dims.error != null) {
      setValidationError(dims.error);
      return;
    }
    setValidationError(null);
    onSubmit({
      accountType: form.accountType,
      ...dims,
      discountType: form.discountType,
      discountValue,
      priority,
      minAdvanceBookingWindow,
      minLengthOfStay,
    });
  };

  const isEdit = initialRule != null;
  const error = validationError ?? submitError;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit scope rule' : 'New scope rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isEdit && (
            <Alert severity="info">
              Grants are immutable — saving archives this rule and creates a replacement with a new id. Tokens already
              minted against the old id keep honouring.
            </Alert>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel id="scope-rule-account">Account</InputLabel>
            <Select
              labelId="scope-rule-account"
              label="Account"
              value={form.accountType}
              disabled={isEdit}
              onChange={(event) => set('accountType', event.target.value)}
            >
              {GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            Scope — pick several values to match any of them; leave a dimension empty to match everything on it.
          </Typography>

          <ScopeDimensionFields
            idPrefix="scope-rule"
            value={form}
            onChange={set}
            countriesEmptyHelper="Empty = all landing countries"
          />

          <Typography variant="caption" color="text.secondary">
            Discount — priority 1 wins first when several rules match.
          </Typography>

          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={form.discountType}
            onChange={(_, value: GHAScopeRuleDiscountType | null) => {
              if (value != null) {
                set('discountType', value);
              }
            }}
          >
            {GHA_SCOPE_DISCOUNT_TYPE_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={{ whiteSpace: 'nowrap' }}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              fullWidth
              label="Value (%)"
              value={form.discountValue}
              onChange={(event) => set('discountValue', event.target.value)}
            />
            <TextField
              size="small"
              fullWidth
              label="Priority"
              value={form.priority}
              onChange={(event) => set('priority', event.target.value)}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Eligibility minimums (optional) — the defaults match the whole estate today.
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              fullWidth
              label="Min ABW (days)"
              value={form.minAdvanceBookingWindow}
              onChange={(event) => set('minAdvanceBookingWindow', event.target.value)}
            />
            <TextField
              size="small"
              fullWidth
              label="Min LOS (nights)"
              value={form.minLengthOfStay}
              onChange={(event) => set('minLengthOfStay', event.target.value)}
            />
          </Stack>

          {error != null && <Alert severity="error">{error}</Alert>}
          <FormHelperText>
            The (value, country) pair must be registered in Hotel Center first — an unregistered pair is rejected on
            save with the registration instruction, checked per listed country.
          </FormHelperText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
          {isEdit ? 'Replace rule' : 'Create rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
